<!--
  @component
  A variant of the `<Select>` widget with an auto-complete text input field.
  @see https://w3c.github.io/aria/#combobox
  @see https://www.w3.org/WAI/ARIA/apg/patterns/combobox/
  @todo Add DOM API compatibility.
-->
<script>
  import { _ } from '@sveltia/i18n';
  import { getSelectedItemDetail } from '../../services/select.svelte.js';
  import Button from '../button/button.svelte';
  import Icon from '../icon/icon.svelte';
  import Listbox from '../listbox/listbox.svelte';
  import SearchBar from '../text-field/search-bar.svelte';
  import TextInput from '../text-field/text-input.svelte';
  import TruncatedText from '../typography/truncated-text.svelte';
  import Popup from '../util/popup.svelte';

  /**
   * @import { ComboboxProps, TextInputProps } from '../../typedefs';
   */

  /**
   * Selector for the currently selected option in the popup. Used to update the selected option
   * when the value is changed externally.
   */
  const SELECTED_SELECTOR = '[role="option"][aria-selected="true"]';

  /**
   * @type {ComboboxProps & TextInputProps & Record<string, any>}
   */
  let {
    /* eslint-disable prefer-const */
    value = $bindable(),
    class: className,
    hidden = false,
    disabled = false,
    readonly = false,
    required = false,
    invalid = false,
    editable = true,
    position = 'bottom-left',
    filterThreshold = 5,
    children,
    chevronIcon,
    onChange,
    ...restProps
    /* eslint-enable prefer-const */
  } = $props();

  const id = $props.id();
  let isPopupOpen = $state(false);

  /** @type {HTMLElement | undefined} */
  let comboboxElement = $state();
  /** @type {HTMLInputElement | undefined} */
  let inputElement = $state();
  /** @type {HTMLElement | undefined} */
  let popupContent = $state();
  /** @type {string} */
  let label = $state('');
  /** @type {boolean} */
  let showFilter = $state(false);
  /** @type {string} */
  let searchTerms = $state('');
  /** @type {boolean} */
  let hasMatchingOptions = $state(true);
  /** @type {HTMLElement} */
  const anchor = $derived(/** @type {HTMLElement} */ (comboboxElement ?? inputElement));

  /**
   * Update the {@link label} and selected option when the {@link value} is changed.
   */
  const _onChange = () => {
    const target = /** @type {HTMLButtonElement | null} */ (
      popupContent?.querySelector(`[role="option"][data-value="${value}"]`)
    );

    if (target) {
      label = target.dataset.label || target.dataset.value || target.textContent || '';

      if (target.getAttribute('aria-selected') !== 'true') {
        popupContent?.querySelector(SELECTED_SELECTOR)?.setAttribute('aria-selected', 'false');
        target.setAttribute('aria-selected', 'true');
      }
    }
  };

  /**
   * Update the {@link value} whenever an option is selected.
   * @param {HTMLButtonElement} target Selected option.
   */
  const _onSelect = (target) => {
    const detail = getSelectedItemDetail(target);

    value = detail.value;

    _onChange();
    onChange?.(new CustomEvent('Change', { detail }));
  };

  $effect(() => {
    if (popupContent) {
      globalThis.requestAnimationFrame(() => {
        const selected = popupContent?.querySelector(SELECTED_SELECTOR);

        if (selected) {
          _onSelect(/** @type {HTMLButtonElement} */ (selected));
        }
      });
    }
  });

  $effect(() => {
    void value;
    _onChange();
  });
</script>

<div {...restProps} role="none" class="sui combobox {className}" class:editable {hidden}>
  {#if !editable}
    <div
      bind:this={comboboxElement}
      {...restProps}
      role="combobox"
      {id}
      class:selected={value !== undefined}
      tabindex={disabled ? -1 : 0}
      aria-controls="{id}-popup"
      aria-expanded={isPopupOpen}
      aria-hidden={hidden}
      aria-disabled={disabled}
      aria-readonly={readonly}
      aria-required={required}
      aria-invalid={invalid}
      aria-haspopup="listbox"
    >
      <div role="none" class="label">
        <TruncatedText>
          {value !== undefined ? label : _('_sui.combobox.select_an_option')}
        </TruncatedText>
      </div>
    </div>
  {:else}
    <TextInput
      {...restProps}
      dir="auto"
      bind:element={inputElement}
      role="combobox"
      {id}
      value={value === undefined ? '' : String(value)}
      {hidden}
      {disabled}
      {readonly}
      {required}
      {invalid}
      aria-controls="{id}-popup"
      aria-expanded={isPopupOpen}
      aria-haspopup="listbox"
    />
  {/if}
  <Button
    variant="ghost"
    iconic
    {hidden}
    {disabled}
    tabindex={!editable || readonly || disabled ? -1 : 0}
    aria-label={isPopupOpen ? _('_sui.collapse') : _('_sui.expand')}
    aria-controls="{id}-popup"
    aria-expanded={isPopupOpen}
    onclick={(event) => {
      event.preventDefault();
      event.stopPropagation();

      if (!disabled && !readonly) {
        isPopupOpen = !isPopupOpen;
      }
    }}
  >
    {#snippet startIcon()}
      {#if chevronIcon}
        {@render chevronIcon()}
      {:else}
        <Icon name="expand_more" />
      {/if}
    {/snippet}
  </Button>
</div>
<Popup
  bind:content={popupContent}
  id="{id}-popup"
  class="combobox"
  {anchor}
  {position}
  touchOptimized={true}
  bind:open={isPopupOpen}
  onOpen={() => {
    showFilter =
      filterThreshold === -1
        ? false
        : (popupContent?.querySelectorAll('[role="option"]')?.length ?? 0) > filterThreshold;
    searchTerms = '';
  }}
>
  <div role="none" class="combobox-inner">
    {#if showFilter}
      <SearchBar
        flex
        aria-label={_('_sui.combobox.filter_options')}
        aria-controls="{id}-listbox"
        bind:value={searchTerms}
        onkeydown={(event) => {
          if (['ArrowUp', 'ArrowDown', 'Enter'].includes(event.key)) {
            event.preventDefault();
            popupContent
              ?.querySelector('.sui.listbox')
              ?.dispatchEvent(new KeyboardEvent('keydown', event));
          }
        }}
      />
    {/if}
    <Listbox
      id="{id}-listbox"
      class="in-combobox"
      {searchTerms}
      onclick={(event) => {
        if (/** @type {HTMLElement} */ (event.target).matches('[role="option"]')) {
          _onSelect(/** @type {HTMLButtonElement} */ (event.target));
        }
      }}
      onFilter={(event) => {
        hasMatchingOptions = !!(/** @type {CustomEvent} */ (event).detail.matched);
      }}
    >
      {@render children?.()}
    </Listbox>
    {#if !hasMatchingOptions}
      <div role="alert" class="no-options">
        {_('_sui.combobox.no_matching_options')}
      </div>
    {/if}
  </div>
</Popup>

<style>.combobox {
  margin: var(--sui-focus-ring-width);
  display: flex;
  align-items: center;
  position: relative;
  min-width: var(--sui-combobox-min-width, calc(var(--sui-option-height) * 5));
}
.combobox div[role=combobox] {
  display: flex;
  align-items: center;
  border-width: 1px;
  border-color: var(--sui-control-border-color);
  border-radius: var(--sui-textbox-border-radius);
  padding-block: 0;
  padding-inline-start: calc(var(--sui-textbox-height) / 4);
  padding-inline-end: var(--sui-textbox-height);
  width: 100%;
  height: var(--sui-textbox-height);
  color: var(--sui-control-foreground-color);
  background-color: var(--sui-disabled-background-color);
  font-family: var(--sui-control-font-family);
  font-size: var(--sui-control-font-size);
  line-height: var(--sui-control-line-height);
  -webkit-user-select: none;
  user-select: none;
  cursor: pointer;
  transition: all 200ms;
}
.combobox div[role=combobox]:not(.selected) {
  font-style: italic;
}
.combobox div[role=combobox]:is(:hover, :focus) {
  background-color: var(--sui-hover-background-color);
}
.combobox div[role=combobox][aria-invalid=true] {
  border-color: var(--sui-error-border-color);
}
.combobox div[role=combobox] .label {
  width: 100%;
}
.combobox :global(.icon) {
  font-size: var(--sui-font-size-xx-large);
  opacity: 0.5;
}
.combobox > :global(.icon) {
  position: absolute;
  inset-block-start: 8px;
  inset-inline-start: 8px;
  z-index: 1;
}
.combobox > :global(button) {
  position: absolute;
  inset-block-start: 0;
  inset-inline-end: 0;
  z-index: 1;
  margin: 0 !important;
  border-start-start-radius: 0;
  border-end-start-radius: 0;
  background-color: transparent !important;
}
.combobox > :global(button[tabindex="-1"]) {
  pointer-events: none;
}
.combobox :global(:not(.editable)) > :global(button) {
  background-color: transparent !important;
}
.combobox :global(.text-input) {
  margin: 0 !important;
  width: 100% !important;
}
.combobox :global(input) {
  padding-block: 0;
  padding-inline: 8px 32px;
  width: 0;
}
.combobox + :global([role=listbox]) {
  position: fixed;
  z-index: 100;
  border-radius: var(--sui-control-medium-border-radius);
  box-shadow: 0 8px 16px var(--sui-popup-shadow-color);
  overflow: auto;
  background-color: var(--sui-secondary-background-color);
  -webkit-backdrop-filter: blur(32px);
  backdrop-filter: blur(32px);
  /* Add .1s delay before the position can be determined */
  transition: opacity 100ms 100ms;
}
.combobox + :global([role=listbox]:not(.open)) {
  opacity: 0;
  pointer-events: none;
}

.combobox-inner {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.combobox-inner :global(.sui.search-bar) {
  flex: none;
}
.combobox-inner :global(.sui.listbox) {
  flex: auto;
  overflow-y: auto;
}
.combobox-inner .no-options {
  flex: none;
  display: flex;
  align-items: center;
  padding: var(--sui-option-padding);
  height: var(--sui-option-height);
  color: var(--sui-tertiary-foreground-color);
}</style>
